"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Philosophy() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  const headlineLines = ["Precision is", "the foundation.", "Efficiency is", "the outcome."];
  const commitments = [{
    index: "01.",
    id: "architectural-integrity",
    body: "Every system we build begins with a deep audit of your existing operations. We interrogate your current workflows before we write a single line of configuration — verifying data integrity, mapping dependencies, and documenting the logic that drives your business. A backend that cannot explain its own structure is not a solution; it is a liability. Our practice insists on total transparency at every stage, ensuring your infrastructure remains robust and scalable."
  }, {
    index: "02.",
    id: "operational-hierarchy",
    body: "Operational data has weight, and that weight must be visible to the decision-maker. We construct digital hierarchies that move your team from the consequential to the contextual without friction — through intuitive dashboards, automated reporting, and the disciplined removal of manual bottlenecks. A well-ordered system does not ask you to search for answers; it presents the metrics you need in the order your business demands."
  }, {
    index: "03.",
    id: "restraint-in-integration",
    body: "We add nothing for its own sake. Software features are reserved for functionality that drives growth; bloat is refused entirely. Where a simple automation will do the work of a complex plugin, we choose the simple path. Where silence in your notification stream carries more value than constant alerts, we prioritize focus. Restraint is not minimalism — it is the conviction that your team's attention is your most valuable asset."
  }];
  return <section data-section-id="15514" id="our-philosophy" ref={sectionRef} className="dark bg-background text-foreground py-28 md:py-36">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12">
        {}
        <div className="mb-20 md:mb-28">
          <div className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground mb-4" style={{
          opacity: isVisible ? 1 : 0,
          transition: "opacity 520ms ease-out"
        }}>
            Our Philosophy
          </div>
          <div className="h-px bg-border w-full origin-left" style={{
          transform: isVisible ? "scaleX(1)" : "scaleX(0)",
          transition: "transform 640ms ease-out"
        }} />
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-6">
          {}
          <div className="md:col-span-4">
            <div className="md:sticky md:top-28">
              <h2 className="font-default font-medium text-foreground" style={{
              fontSize: "clamp(2rem, 3.4vw, 3.25rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.04
            }}>
                {headlineLines.map((line, i) => <span key={i} data-index={i} className="block" style={{
                paddingLeft: `${i * 0.75}rem`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateY(0)" : "translateY(24px)",
                transition: `opacity 520ms cubic-bezier(0.2, 0.6, 0.2, 1) ${i * 80}ms, transform 520ms cubic-bezier(0.2, 0.6, 0.2, 1) ${i * 80}ms`
              }}>
                    {line}
                  </span>)}
              </h2>
            </div>
          </div>

          {}
          <div className="md:col-span-8 md:col-start-5">
            <div className="flex flex-col gap-12">
              {commitments.map((item, i) => <div key={item.id} data-index={i} id={item.id} style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 520ms cubic-bezier(0.2, 0.6, 0.2, 1) ${400 + i * 200}ms, transform 520ms cubic-bezier(0.2, 0.6, 0.2, 1) ${400 + i * 200}ms`
            }}>
                  {}
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-mono text-[11px] font-medium tracking-[0.12em] text-muted-foreground">
                      {item.index}
                    </span>
                    <span className="h-px bg-border block" style={{
                  width: "64px"
                }} />
                  </div>

                  <p className="font-default font-normal text-foreground max-w-[640px]" style={{
                fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
                letterSpacing: "-0.005em",
                lineHeight: 1.55
              }}>
                    {item.body}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}